import {useMemo, useState} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import {Trans, useTranslation} from 'react-i18next'
import ContactUsButton from '../components/ContactUsButton'
import './FaqPage.css'

type Category = 'start' | 'billing' | 'editor' | 'sharing' | 'privacy'
type CategoryFilter = Category | 'all'

interface FaqEntry {
  readonly cat: Category
  readonly key: string
}

const FAQ_ITEMS: readonly FaqEntry[] = [
  {cat: 'start', key: 'account'},
  {cat: 'start', key: 'free'},
  {cat: 'start', key: 'devices'},
  {cat: 'billing', key: 'cost'},
  {cat: 'billing', key: 'expire'},
  {cat: 'billing', key: 'refund'},
  {cat: 'billing', key: 'invoice'},
  {cat: 'editor', key: 'batch'},
  {cat: 'editor', key: 'mobile'},
  {cat: 'editor', key: 'screenImage'},
  {cat: 'editor', key: 'renderHistory'},
  {cat: 'sharing', key: 'screenPrivacy'},
  {cat: 'sharing', key: 'shareLink'},
  {cat: 'sharing', key: 'forkReward'},
  {cat: 'privacy', key: 'storage'},
  {cat: 'privacy', key: 'aiTraining'},
  {cat: 'privacy', key: 'retention'},
]

const CATEGORIES: readonly CategoryFilter[] = ['all', 'start', 'billing', 'editor', 'sharing', 'privacy']

const PlusIcon = ({size = 14}: {size?: number}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
)

// Shared <Trans> components for answer bodies. Every link slot used in any answer
// is declared here so each answer can opt-in by tag name.
const ANSWER_COMPONENTS = {
  free: <RouterLink to="/free" className="fq-inline-link" />,
  refund: <RouterLink to="/refund" className="fq-inline-link" />,
  account: <RouterLink to="/account" className="fq-inline-link" />,
  history: <RouterLink to="/render-history" className="fq-inline-link" />,
  projects: <RouterLink to="/projects" className="fq-inline-link" />,
  privacy: <RouterLink to="/privacy" className="fq-inline-link" />,
  em: <em />,
}

interface FaqItemProps {
  item: FaqEntry
  isOpen: boolean
  onToggle: () => void
}

const FaqItem = ({item, isOpen, onToggle}: FaqItemProps) => {
  const {t} = useTranslation()
  return (
    <div className={`fq-item ${isOpen ? 'open' : ''}`}>
      <button type="button" className="fq-item-btn" onClick={onToggle} aria-expanded={isOpen}>
        <span>{t(`faq.groups.${item.cat}.${item.key}.q`)}</span>
        <span className="fq-item-icon" aria-hidden>
          <PlusIcon size={12} />
        </span>
      </button>
      {isOpen && (
        <div className="fq-item-body">
          <Trans i18nKey={`faq.groups.${item.cat}.${item.key}.a`} components={ANSWER_COMPONENTS} />
        </div>
      )}
    </div>
  )
}

const FaqPage = () => {
  const {t} = useTranslation()
  const [activeCat, setActiveCat] = useState<CategoryFilter>('all')
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({})

  const grouped = useMemo<readonly [Category, FaqEntry[]][]>(() => {
    const filtered = FAQ_ITEMS.filter((entry) => activeCat === 'all' || entry.cat === activeCat)
    const byCat = new Map<Category, FaqEntry[]>()
    for (const entry of filtered) {
      const list = byCat.get(entry.cat) ?? []
      list.push(entry)
      byCat.set(entry.cat, list)
    }
    return [...byCat.entries()]
  }, [activeCat])

  const handleToggle = (id: string) => setOpenIds((prev) => ({...prev, [id]: !prev[id]}))

  return (
    <div className="faq-root">
      <div className="fq-grid" aria-hidden />

      <main className="faq-page">
        <header className="fq-hero">
          <span className="fq-eyebrow">
            <span className="fq-eyebrow-dot" />
            {t('faq.eyebrow')}
          </span>
          <h1 className="fq-title">
            {t('faq.titleLead')} <em>{t('faq.titleEm')}</em>
          </h1>
          <p className="fq-lede">
            <Trans
              i18nKey="faq.lede"
              components={{contactLink: <RouterLink to="/contact" className="fq-inline-link" />}}
            />
          </p>
        </header>

        <div className="fq-cats" role="tablist">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={activeCat === cat}
              className={`fq-cat ${activeCat === cat ? 'active' : ''}`}
              onClick={() => setActiveCat(cat)}
            >
              {t(`faq.categories.${cat}`)}
            </button>
          ))}
        </div>

        <div className="fq-groups">
          {grouped.length === 0 ? (
            <div className="fq-empty">{t('faq.empty')}</div>
          ) : (
            grouped.map(([cat, items]) => (
              <section key={cat} className="fq-group">
                <h2 className="fq-group-title">{t(`faq.categories.${cat}`)}</h2>
                <div>
                  {items.map((entry) => {
                    const id = `${entry.cat}-${entry.key}`
                    return <FaqItem key={id} item={entry} isOpen={!!openIds[id]} onToggle={() => handleToggle(id)} />
                  })}
                </div>
              </section>
            ))
          )}
        </div>

        <section className="fq-cta">
          <div className="fq-cta-text">
            <h3 className="fq-cta-title">{t('faq.ctaTitle')}</h3>
            <p className="fq-cta-body">{t('faq.ctaBody')}</p>
          </div>
          <ContactUsButton />
        </section>
      </main>
    </div>
  )
}

export default FaqPage
